import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { CartState, CartItem, Customer, AppliedPromotion } from '@/types';
import { generateLineId, getRemainingQuantity } from '@/utils/cartLines';

const VOUCHER_SECTION_KEY = 'pos_voucher_section_enabled';

// Initial state
const initialState: CartState = {
  items: [],
  customer: null,
  discount: 0,
  subtotal: 0,
  tax: 0,
  total: 0,
  taxRate: 0,
  continuingTransactionId: undefined,
  outletId: null,
  outletLocked: false,
  loyaltyPointsToRedeem: 0,
  loyaltyDiscountAmount: 0,
  appliedItemPromotions: {},
  appliedCartPromotion: null,
  appliedVoucher: null,
  promotionDiscountAmount: 0,
  voucherSectionEnabled: localStorage.getItem(VOUCHER_SECTION_KEY) !== 'false',
  pendingPaymentMethodPromotions: [],
  userSelectedCartPromotionId: null,
  deselectedItemPromotionProductIds: [],
};

// Helper functions
const calculateTotals = (state: CartState) => {
  state.subtotal = state.items.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  // Aggregate promotion discount from item, cart, and voucher promotions
  state.promotionDiscountAmount =
    Object.values(state.appliedItemPromotions).reduce((s, p) => s + p.discountAmount, 0) +
    (state.appliedCartPromotion?.discountAmount ?? 0) +
    (state.appliedVoucher?.discountAmount ?? 0);

  // Tax applies to net amount after ALL discounts (manual + loyalty + promotion)
  const taxableAmount = Math.max(
    0,
    state.subtotal - state.discount - (state.loyaltyDiscountAmount || 0) - state.promotionDiscountAmount
  );
  state.tax = taxableAmount * (state.taxRate / 100);
  state.total = Math.max(0, taxableAmount + state.tax);
};

const normalizeQuantity = (
  value: number,
  availableStock?: number,
  options: { enforceStock?: boolean } = {}
) => {
  const { enforceStock = true } = options;

  if (!Number.isFinite(value)) {
    return 0;
  }

  const clampedValue = Math.max(0, value);
  let normalizedHundredths = Math.round(clampedValue * 100);

  if (enforceStock && typeof availableStock === 'number' && Number.isFinite(availableStock)) {
    const maxHundredths = Math.floor((availableStock + 1e-6) * 100);
    normalizedHundredths = Math.min(normalizedHundredths, maxHundredths);
  }

  return Number((normalizedHundredths / 100).toFixed(2));
};

const normalizeCartItems = (items: CartItem[], options?: { enforceStock?: boolean }) =>
  items.map((item) => ({
    ...item,
    quantity: normalizeQuantity(item.quantity, item.availableStock, options),
  }));

/** Cart line as supplied by callers; the line identifier is generated when omitted. */
export type CartItemInput = Omit<CartItem, 'lineId'> & { lineId?: string };

/**
 * Attaches a unique line identifier to every item that lacks one.
 */
const withLineIds = (items: CartItemInput[]): CartItem[] =>
  items.map((item) => ({ ...item, lineId: item.lineId || generateLineId() }));

/**
 * Highest quantity a line may hold given the stock already consumed by the other
 * lines of the same product. Returns undefined when the line has no usable stock figure.
 */
const getLineCap = (
  items: CartItem[],
  line: Pick<CartItem, 'productId' | 'availableStock'>,
  excludeLineId?: string
): number | undefined =>
  typeof line.availableStock === 'number' && Number.isFinite(line.availableStock)
    ? getRemainingQuantity(items, line.productId, line.availableStock, excludeLineId)
    : undefined;

/**
 * Removes the product's item promotion when no cart line references it anymore.
 */
const pruneItemPromotion = (state: CartState, productId: string) => {
  if (!state.items.some((i) => i.productId === productId)) {
    delete state.appliedItemPromotions[productId];
  }
};

// Cart slice
export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addItem: {
      reducer: (state, action: PayloadAction<Omit<CartItem, 'quantity'> & { quantity?: number }>) => {
        const { quantity = 1, ...itemData } = action.payload;
        const existingItem = itemData.alwaysNewLine
          ? undefined
          : state.items.find(
              (item) =>
                item.productId === itemData.productId &&
                (item.unitId || null) === (itemData.unitId || null)
            );

        if (existingItem) {
          const cap = getLineCap(state.items, existingItem, existingItem.lineId);
          const normalized = normalizeQuantity(existingItem.quantity + quantity, cap);

          if (normalized > existingItem.quantity) {
            existingItem.quantity = normalized;
          }
        } else {
          const normalized = normalizeQuantity(quantity, getLineCap(state.items, itemData));

          if (normalized > 0) {
            state.items.push({
              ...itemData,
              quantity: normalized,
            });
          }
        }

        // Lock outlet when cart has items
        if (state.items.length > 0) {
          state.outletLocked = true;
        }

        calculateTotals(state);
      },
      prepare: (item: Omit<CartItemInput, 'quantity'> & { quantity?: number }) => ({
        payload: { ...item, lineId: item.lineId || generateLineId() },
      }),
    },

    removeItem: (state, action: PayloadAction<{ lineId: string }>) => {
      const line = state.items.find((item) => item.lineId === action.payload.lineId);
      if (!line) return;
      state.items = state.items.filter((item) => item.lineId !== line.lineId);
      pruneItemPromotion(state, line.productId);
      calculateTotals(state);
    },

    updateQuantity: (state, action: PayloadAction<{ lineId: string; quantity: number }>) => {
      const { lineId, quantity } = action.payload;
      const item = state.items.find((i) => i.lineId === lineId);

      if (item) {
        const normalized = normalizeQuantity(quantity, getLineCap(state.items, item, lineId));

        if (normalized <= 0) {
          state.items = state.items.filter((i) => i.lineId !== lineId);
          pruneItemPromotion(state, item.productId);
        } else {
          item.quantity = normalized;
        }
      }

      calculateTotals(state);
    },

    updateItemNote: (state, action: PayloadAction<{ lineId: string; note: string }>) => {
      const item = state.items.find((i) => i.lineId === action.payload.lineId);

      if (item) {
        item.note = action.payload.note;
      }
    },

    updateItemPrice: (state, action: PayloadAction<{ lineId: string; price: number }>) => {
      const item = state.items.find((i) => i.lineId === action.payload.lineId);

      if (item && item.allowPriceEdit) {
        item.price = Math.max(0, action.payload.price);
        calculateTotals(state);
      }
    },

    /**
     * Inserts a copy of a line right after it with quantity 1, an empty note and
     * a new line identifier. The copy starts at price 0 when the source line allows
     * price editing, so the cashier enters its own price; a fixed-price line keeps
     * its price because the cashier cannot change it.
     */
    duplicateLine: {
      reducer: (state, action: PayloadAction<{ lineId: string; newLineId: string }>) => {
        const index = state.items.findIndex((i) => i.lineId === action.payload.lineId);
        if (index < 0) return;

        const source = state.items[index];
        const quantity = normalizeQuantity(1, getLineCap(state.items, source));
        if (quantity <= 0) return;

        state.items.splice(index + 1, 0, {
          ...source,
          lineId: action.payload.newLineId,
          price: source.allowPriceEdit ? 0 : source.price,
          quantity,
          note: '',
        });
        calculateTotals(state);
      },
      prepare: (payload: { lineId: string }) => ({
        payload: { lineId: payload.lineId, newLineId: generateLineId() },
      }),
    },

    setCustomer: (state, action: PayloadAction<Customer | null>) => {
      state.customer = action.payload;
      // Clear loyalty redemption when customer changes
      state.loyaltyPointsToRedeem = 0;
      state.loyaltyDiscountAmount = 0;
      // Clear voucher and selected cart promo when customer changes — customer-specific conditions may no longer apply
      state.appliedVoucher = null;
      state.userSelectedCartPromotionId = null;
      state.appliedCartPromotion = null;
      calculateTotals(state);
    },

    setDiscount: (state, action: PayloadAction<number>) => {
      state.discount = Math.max(0, action.payload);
      calculateTotals(state);
    },

    setTaxRate: (state, action: PayloadAction<number>) => {
      state.taxRate = Math.max(0, action.payload);
      calculateTotals(state);
    },

    setOutlet: (state, action: PayloadAction<string | null>) => {
      // Only allow outlet change if cart is empty (not locked)
      if (!state.outletLocked || state.items.length === 0) {
        state.outletId = action.payload;
        state.outletLocked = false;
      }
    },

    clearCart: (state) => {
      state.items = [];
      state.customer = null;
      state.discount = 0;
      state.continuingTransactionId = undefined;
      state.outletLocked = false;
      state.loyaltyPointsToRedeem = 0;
      state.loyaltyDiscountAmount = 0;
      state.appliedItemPromotions = {};
      state.appliedCartPromotion = null;
      state.appliedVoucher = null;
      state.promotionDiscountAmount = 0;
      state.pendingPaymentMethodPromotions = [];
      state.userSelectedCartPromotionId = null;
      state.deselectedItemPromotionProductIds = [];
      calculateTotals(state);
    },

    // Clear only the link to a pending transaction; keep cart content
    clearContinuingTransaction: (state) => {
      state.continuingTransactionId = undefined;
    },

    loadCart: {
      reducer: (state, action: PayloadAction<{
        items: CartItem[];
        customer?: Customer | null;
        discount?: number;
        continuingTransactionId?: string;
        outletId?: string | null;
      }>) => {
        const { items, customer = null, discount = 0, continuingTransactionId, outletId } = action.payload;
        state.items = normalizeCartItems(items, { enforceStock: false });
        state.customer = customer;
        state.discount = discount;
        state.continuingTransactionId = continuingTransactionId;
        if (outletId !== undefined) {
          state.outletId = outletId;
        }
        // Lock outlet if cart has items
        state.outletLocked = state.items.length > 0;
        calculateTotals(state);
      },
      prepare: (payload: {
        items: CartItemInput[];
        customer?: Customer | null;
        discount?: number;
        continuingTransactionId?: string;
        outletId?: string | null;
      }) => ({
        payload: { ...payload, items: withLineIds(payload.items) },
      }),
    },

    // Bulk operations
    setItems: {
      reducer: (state, action: PayloadAction<CartItem[]>) => {
        state.items = normalizeCartItems(action.payload, { enforceStock: false });
        calculateTotals(state);
      },
      prepare: (items: CartItemInput[]) => ({ payload: withLineIds(items) }),
    },

    increaseQuantity: (state, action: PayloadAction<{ lineId: string }>) => {
      const item = state.items.find((i) => i.lineId === action.payload.lineId);
      if (item) {
        const step = Number.isInteger(item.quantity) ? 1 : 0.01;
        const normalized = normalizeQuantity(
          item.quantity + step,
          getLineCap(state.items, item, item.lineId)
        );

        if (normalized > item.quantity) {
          item.quantity = normalized;
          calculateTotals(state);
        }
      }
    },

    decreaseQuantity: (state, action: PayloadAction<{ lineId: string }>) => {
      const item = state.items.find((i) => i.lineId === action.payload.lineId);
      if (item) {
        const step = Number.isInteger(item.quantity) ? 1 : 0.01;
        const normalized = normalizeQuantity(item.quantity - step);

        if (normalized <= 0) {
          state.items = state.items.filter((i) => i.lineId !== item.lineId);
          pruneItemPromotion(state, item.productId);
        } else {
          item.quantity = normalized;
        }
        calculateTotals(state);
      }
    },

    /**
     * Switches a line to another selling unit. A line of a regular item is merged
     * into an existing line of the same product that already uses the target unit
     * (quantities are summed and the target line keeps its price). Lines of
     * always-new-line items are simply switched.
     */
    updateItemUnit: (
      state,
      action: PayloadAction<{
        lineId: string;
        unit: string;
        unitId?: string;
        price: number;
        availableStock?: number;
      }>
    ) => {
      const { lineId, unit, unitId, price, availableStock } = action.payload;
      const item = state.items.find((i) => i.lineId === lineId);
      if (!item) return;

      const target = item.alwaysNewLine
        ? undefined
        : state.items.find(
            (i) =>
              i.lineId !== lineId &&
              i.productId === item.productId &&
              (i.unitId || null) === (unitId || null)
          );

      if (target) {
        const otherLines = state.items.filter((i) => i.lineId !== lineId);
        const cap = getLineCap(otherLines, target, target.lineId);
        const normalized = normalizeQuantity(target.quantity + item.quantity, cap);
        if (normalized > target.quantity) {
          target.quantity = normalized;
        }
        state.items = state.items.filter((i) => i.lineId !== lineId);
      } else {
        item.unit = unit;
        item.unitId = unitId;
        item.price = Math.max(0, price);
        if (availableStock !== undefined) {
          item.availableStock = availableStock;
        }
        const normalized = normalizeQuantity(item.quantity, getLineCap(state.items, item, lineId));
        if (normalized > 0) {
          item.quantity = normalized;
        }
      }
      calculateTotals(state);
    },

    setLoyaltyRedemption: (state, action: PayloadAction<{ points: number; discountAmount: number }>) => {
      state.loyaltyPointsToRedeem = action.payload.points;
      state.loyaltyDiscountAmount = action.payload.discountAmount;
      calculateTotals(state);
    },

    clearLoyaltyRedemption: (state) => {
      state.loyaltyPointsToRedeem = 0;
      state.loyaltyDiscountAmount = 0;
      calculateTotals(state);
    },

    setItemPromotion(state, action: PayloadAction<{ productId: string; promotion: AppliedPromotion }>) {
      state.appliedItemPromotions[action.payload.productId] = action.payload.promotion;
      calculateTotals(state);
    },

    clearItemPromotion(state, action: PayloadAction<string>) {
      delete state.appliedItemPromotions[action.payload];
      calculateTotals(state);
    },

    setCartPromotion(state, action: PayloadAction<AppliedPromotion | null>) {
      state.appliedCartPromotion = action.payload;
      calculateTotals(state);
    },

    setVoucher(state, action: PayloadAction<AppliedPromotion | null>) {
      state.appliedVoucher = action.payload;
      calculateTotals(state);
    },

    clearVoucher(state) {
      state.appliedVoucher = null;
      calculateTotals(state);
    },

    clearAllPromotions(state) {
      state.appliedItemPromotions = {};
      state.appliedCartPromotion = null;
      state.appliedVoucher = null;
      state.promotionDiscountAmount = 0;
      state.pendingPaymentMethodPromotions = [];
      state.userSelectedCartPromotionId = null;
      state.deselectedItemPromotionProductIds = [];
      calculateTotals(state);
    },

    setSelectedCartPromotion(state, action: PayloadAction<string | null>) {
      state.userSelectedCartPromotionId = action.payload;
    },

    toggleItemPromotionOptOut(state, action: PayloadAction<string>) {
      const productId = action.payload;
      const idx = state.deselectedItemPromotionProductIds.indexOf(productId);
      if (idx >= 0) {
        state.deselectedItemPromotionProductIds.splice(idx, 1);
      } else {
        state.deselectedItemPromotionProductIds.push(productId);
      }
    },

    setDeselectedItemPromotions(state, action: PayloadAction<string[]>) {
      state.deselectedItemPromotionProductIds = action.payload;
    },

    setPendingPaymentMethodPromotions(state, action: PayloadAction<Array<AppliedPromotion & { requiredPaymentMethod: string }>>) {
      state.pendingPaymentMethodPromotions = action.payload;
    },

    setVoucherSectionEnabled(state, action: PayloadAction<boolean>) {
      state.voucherSectionEnabled = action.payload;
      localStorage.setItem(VOUCHER_SECTION_KEY, String(action.payload));
    },

    // Reset cart to initial state (used on logout)
    resetCart: () => initialState,
  },
  extraReducers: (builder) => {
    // Clear cart on logout
    builder.addMatcher(
      (action) => action.type === 'auth/logout/fulfilled' || action.type === 'auth/clearAuth',
      () => initialState
    );
  },
});

export const {
  addItem,
  removeItem,
  updateQuantity,
  updateItemNote,
  updateItemPrice,
  updateItemUnit,
  duplicateLine,
  setCustomer,
  setDiscount,
  setTaxRate,
  setOutlet,
  clearCart,
  clearContinuingTransaction,
  loadCart,
  setItems,
  increaseQuantity,
  decreaseQuantity,
  setLoyaltyRedemption,
  clearLoyaltyRedemption,
  setItemPromotion,
  clearItemPromotion,
  setCartPromotion,
  setVoucher,
  clearVoucher,
  clearAllPromotions,
  setSelectedCartPromotion,
  toggleItemPromotionOptOut,
  setDeselectedItemPromotions,
  setPendingPaymentMethodPromotions,
  setVoucherSectionEnabled,
  resetCart,
} = cartSlice.actions;

// Selectors
export const selectCart = (state: { cart: CartState }) => state.cart;
export const selectCartItems = (state: { cart: CartState }) => state.cart.items;
export const selectCartItemsCount = (state: { cart: CartState }) =>
  state.cart.items.reduce((sum, item) => sum + item.quantity, 0);
export const selectCartSubtotal = (state: { cart: CartState }) => state.cart.subtotal;
export const selectCartTotal = (state: { cart: CartState }) => state.cart.total;
export const selectCartCustomer = (state: { cart: CartState }) => state.cart.customer;
export const selectCartDiscount = (state: { cart: CartState }) => state.cart.discount;
export const selectCartTax = (state: { cart: CartState }) => state.cart.tax;
export const selectCartTaxRate = (state: { cart: CartState }) => state.cart.taxRate;
export const selectContinuingTransactionId = (state: { cart: CartState }) => state.cart.continuingTransactionId;
export const selectCartOutletId = (state: { cart: CartState }) => state.cart.outletId;
export const selectCartOutletLocked = (state: { cart: CartState }) => state.cart.outletLocked;
export const selectLoyaltyPointsToRedeem = (state: { cart: CartState }) => state.cart.loyaltyPointsToRedeem;
export const selectLoyaltyDiscountAmount = (state: { cart: CartState }) => state.cart.loyaltyDiscountAmount;
export const selectAppliedItemPromotions = (state: { cart: CartState }) => state.cart.appliedItemPromotions;
export const selectAppliedCartPromotion = (state: { cart: CartState }) => state.cart.appliedCartPromotion;
export const selectAppliedVoucher = (state: { cart: CartState }) => state.cart.appliedVoucher;
export const selectPromotionDiscountAmount = (state: { cart: CartState }) => state.cart.promotionDiscountAmount;
export const selectVoucherSectionEnabled = (state: { cart: CartState }) => state.cart.voucherSectionEnabled;
export const selectPendingPaymentMethodPromotions = (state: { cart: CartState }) => state.cart.pendingPaymentMethodPromotions;
export const selectUserSelectedCartPromotionId = (state: { cart: CartState }) => state.cart.userSelectedCartPromotionId;
export const selectDeselectedItemPromotionProductIds = (state: { cart: CartState }) => state.cart.deselectedItemPromotionProductIds;
